import React, { useState, useEffect } from 'react';
import Button from './Button';
import TextInput from './TextInput';
import CountrySelect from './CountrySelect';
import Checkbox from './Checkbox';
import { useAuth } from '../contexts/AuthContext';

const InstantSearchForm = () => {
  const { authenticatedFetch } = useAuth();
  const [formData, setFormData] = useState({
    applyFrom: '',
    applyTo: '',
    receiverName: '',
    receiverCity: '',
    receiverZip: '',
    receiverCtry: 'HU',
    receiverStreet: '',
    reference: '',
    email: '',
    phone: '',
    searchSender: false,
    parcelNumber: ''
  });

  const [message, setMessage] = useState({ text: '', type: '', visible: false });

  // Disable other fields if parcel number is provided
  const isOtherFieldsDisabled = formData.parcelNumber.length > 0;

  useEffect(() => {
    // Set default dates for last 30 days
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - (30 * 24 * 60 * 60 * 1000));
    
    setFormData(prev => ({
      ...prev,
      applyFrom: thirtyDaysAgo.toISOString().split('T')[0],
      applyTo: today.toISOString().split('T')[0]
    }));
  }, []);

  const handleInputChange = (e) => {
    if (!e || !e.target) {
      console.error('Event or event.target is undefined in handleInputChange');
      return;
    }
    
    const { name, value, type, checked } = e.target;
    
    // Handle parcelNumber validation (only numbers, max 12 characters)
    if (name === 'parcelNumber') {
      const numericValue = value.replace(/\D/g, '').slice(0, 12);
      setFormData(prevState => ({
        ...prevState,
        [name]: numericValue
      }));
      return;
    }
    
    setFormData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const showMessage = (text, type) => {
    setMessage({ text, type, visible: true });
    
    // Hide message after 5 seconds
    setTimeout(() => {
      setMessage(prev => ({ ...prev, visible: false }));
    }, 5000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Send data to the instant search endpoint
      const response = await authenticatedFetch('/api/instant-search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      const result = await response.json();
      
      if (result.success) {
        showMessage('Instant search registered! It can take 10-20 minutes t oreceive results in email.', 'success');
        
        // Reset form but keep the current dates
        setFormData(prev => ({
          ...prev,
          receiverName: '',
          receiverCity: '',
          receiverZip: '',
          receiverCtry: 'HU',
          receiverStreet: '',
          reference: '',
          email: '',
          phone: '',
          searchSender: false,
          parcelNumber: ''
        }));
      } else {
        showMessage('Error: ' + result.message, 'error');
      }
    } catch (error) {
      console.error('Error submitting instant search:', error);
      showMessage('Error: Failed to save instant search criteria. Please try again.', 'error');
    }
  };

  const handleReset = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - (30 * 24 * 60 * 60 * 1000));
    
    setFormData({
      applyFrom: thirtyDaysAgo.toISOString().split('T')[0],
      applyTo: today.toISOString().split('T')[0],
      receiverName: '',
      receiverCity: '',
      receiverZip: '',
      receiverCtry: 'HU',
      receiverStreet: '',
      reference: '',
      email: '',
      phone: '',
      searchSender: false,
      parcelNumber: ''
    });
    
    setMessage({ text: '', type: '', visible: false });
  };

  return (
    <div className="instant-search-form">
      <div className="form-container">
        <h2>Instant Search</h2>
        
        {message.visible && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            {/* Search Options */}
            <div className="form-section">
              <h3>Search Options</h3>
              <div className="form-row">
                <Checkbox
                  name="searchSender"
                  label="Search Sender"
                  checked={formData.searchSender}
                  onChange={handleInputChange}
                />
                <TextInput
                  label="Parcel Number"
                  name="parcelNumber"
                  value={formData.parcelNumber}
                  onChange={handleInputChange}
                  placeholder="Enter parcel number (max 12 digits)"
                  maxLength="12"
                  pattern="\d*"
                  inputMode="numeric"
                />
              </div>
            </div>

            {/* Date Range */}
            <div className="form-section">
              <h3>Date Range</h3>
              <div className="form-row">
                <TextInput
                  label="Apply From"
                  type="date"
                  name="applyFrom"
                  value={formData.applyFrom}
                  onChange={handleInputChange}
                  required
                  disabled={isOtherFieldsDisabled}
                />
                <TextInput
                  label="Apply To"
                  type="date"
                  name="applyTo"
                  value={formData.applyTo}
                  onChange={handleInputChange}
                  required
                  disabled={isOtherFieldsDisabled}
                />
              </div>
            </div>

            {/* Receiver/Sender Information */}
            <div className="form-section" style={formData.searchSender ? {backgroundColor: '#fff0f5'} : {}}>
              <h3>{formData.searchSender ? 'Sender Information' : 'Receiver Information'}</h3>
              <div className="form-row">
                <TextInput
                  label={formData.searchSender ? 'Sender Name' : 'Receiver Name'}
                  name="receiverName"
                  value={formData.receiverName}
                  onChange={handleInputChange}
                  placeholder={formData.searchSender ? 'Enter sender name' : 'Enter receiver name'}
                  disabled={isOtherFieldsDisabled}
                />
                <TextInput
                  label={formData.searchSender ? 'Sender City' : 'Receiver City'}
                  name="receiverCity"
                  value={formData.receiverCity}
                  onChange={handleInputChange}
                  placeholder="Enter city"
                  disabled={isOtherFieldsDisabled}
                />
              </div>
              
              <div className="form-row">
                <TextInput
                  label={formData.searchSender ? 'Sender ZIP' : 'Receiver ZIP'}
                  name="receiverZip"
                  value={formData.receiverZip}
                  onChange={handleInputChange}
                  placeholder="Enter postal code"
                  maxLength="7"
                  disabled={isOtherFieldsDisabled}
                />
                <CountrySelect
                  label={formData.searchSender ? 'Sender Country' : 'Receiver Country'}
                  name="receiverCtry"
                  value={formData.receiverCtry}
                  onChange={handleInputChange}
                  disabled={isOtherFieldsDisabled}
                />
              </div>
              
              <div className="form-row">
                <TextInput
                  label={formData.searchSender ? 'Sender Street' : 'Receiver Street'}
                  name="receiverStreet"
                  value={formData.receiverStreet}
                  onChange={handleInputChange}
                  placeholder="Enter street address"
                  disabled={isOtherFieldsDisabled}
                />
                <TextInput
                  label="Reference"
                  name="reference"
                  value={formData.reference}
                  onChange={handleInputChange}
                  placeholder="Enter reference"
                  disabled={isOtherFieldsDisabled}
                />
              </div>
            </div>

            {/* Contact Information */}
            <div className="form-section">
              <h3>Contact Information</h3>
              <div className="form-row">
                <TextInput
                  label="Email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter email address"
                  disabled={isOtherFieldsDisabled}
                />
                <TextInput
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Enter phone number"
                  disabled={isOtherFieldsDisabled}
                />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <Button type="submit" variant="primary">
              Save Instant Search
            </Button>
            <Button type="button" variant="secondary" onClick={handleReset}>
              Reset Form
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InstantSearchForm;
